import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Hand } from "lucide-react";
import { Button } from "@/components/ui/button";
import { pub, errMsg } from "@/lib/api";
import SigningFlow from "@/components/SigningFlow";
import { LangToggle, LanguageBar, makeT } from "@/lib/i18n";
import { LargePrintToggle, useLargePrint } from "@/lib/largePrint";

export function Ended({ title, body, children, lang, setLang, lp }) {
  return (
    <div className="min-h-screen font-kiosk flex items-center justify-center p-6 ace-watermark" style={{ background: "#FAF8F5" }} data-testid="signing-ended">
      <div className="max-w-lg bg-white rounded-2xl border p-10 text-center fade-in">
        <div className={`flex flex-wrap items-center gap-2 mb-4 ${setLang ? "" : "justify-center"}`}>
          <img src="/ace-mark.png" alt="ACE eSIGN" className={`h-10 w-auto ${setLang ? "mr-auto" : ""}`} data-testid="ended-brand-mark" />
          {setLang && <>{lp && <LargePrintToggle lp={lp} label={makeT(lang)("Large text")} />}<LangToggle lang={lang} setLang={setLang} /></>}
        </div>
        <h1 className="text-3xl font-semibold">{title}</h1>
        <p className="mt-4 text-lg text-slate-600">{body}</p>
        {children}
      </div>
    </div>
  );
}

export function Receipt({ r, children, lang = "en-US" }) {
  const t = makeT(lang);
  return (
    <Ended title={r.status === "declined" ? t("Thank you - staff will follow up") : t("Thank you - you're done")}
      body={r.status === "declined" ? t("rcpt_declined") : r.status === "signed" ? t("rcpt_signed") : t("rcpt_review")}>
      {r.status !== "declined" && <CheckCircle2 className="w-16 h-16 mx-auto mt-6 text-green-600" />}
      <p className="mt-4 font-mono text-sm text-slate-500" data-testid="receipt-id">{t("Receipt")} {r.receipt_id}</p>
      {children}
    </Ended>
  );
}

export default function KioskPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [lang, setLang] = useState("en-US");
  const lp = useLargePrint();
  const setLp = lp[1];
  const t = makeT(lang);
  const load = useCallback(() => pub.get(`/sign/${token}/packet`).then((r) => { setData((prev) => ({ ...r.data, confirmed: prev?.confirmed })); setReceipt(r.data.receipt); if (r.data.language) setLang(r.data.language); setLp(!!r.data.large_print); }).catch((e) => setError(errMsg(e))), [token, setLp]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const block = () => window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", block);
    return () => window.removeEventListener("popstate", block);
  }, []);
  useEffect(() => {
    if (receipt || error) return undefined;
    let t;
    const reset = () => { clearTimeout(t); t = setTimeout(() => { setData(null); setError("Session paused after 5 minutes of inactivity. Please hand the device back to staff."); }, 5 * 60 * 1000); };
    ["pointerdown", "keydown"].forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => { clearTimeout(t); ["pointerdown", "keydown"].forEach((e) => window.removeEventListener(e, reset)); };
  }, [receipt, error]);
  const donePid = data?.packet_id || receipt?.packet_id;
  const backTo = receipt?.status === "awaiting_provider" && donePid ? `/verify/${donePid}` : "/";
  const back = <Button className="mt-8 h-14 px-8 text-lg" variant="outline" onClick={() => nav(backTo, { replace: true })} data-testid="return-to-staff-btn"><Hand className="w-5 h-5 mr-2" />Staff: return to workspace</Button>;
  if (error) return <Ended title={t("Please return the device")} body={error}>{back}</Ended>;
  if (receipt) return <Receipt r={receipt} lang={lang}><p className="mt-6 text-lg">{t("hand_back")}</p>{back}</Receipt>;
  if (!data) return <Ended title={t("Loading...")} body={t("Preparing your documents.")} />;
  if (step0(data)) return <IdentityConfirm data={data} t={t} lang={lang} setLang={setLang} lp={lp} onYes={() => setData({ ...data, confirmed: true })} onNo={() => setError(t("hand_back_help"))} />;
  return <SigningFlow key={data.documents.map((d) => d.template_id).join(",")} token={token} data={data} onFinished={setReceipt} lang={lang} setLang={setLang} lp={lp} onReload={load} />;
}

const step0 = (d) => !d.confirmed;

function IdentityConfirm({ data, onYes, onNo, t, lang, setLang, lp }) {
  return (
    <Ended title={t("Before we begin")} body={t("confirm_details")}>
      <div className="mt-6 text-left"><LanguageBar lang={lang} setLang={setLang} t={t} /></div>
      <div className="mt-4 flex justify-end"><LargePrintToggle lp={lp} label={t("Large text")} /></div>
      <div className="mt-6 text-left bg-slate-50 rounded-xl p-5 text-lg" data-testid="identity-confirm-card">
        <p><span className="text-slate-500">{t("Patient")}:</span> <b>{data.patient_name}</b></p>
        {data.signer.type === "representative" && <p><span className="text-slate-500">{t("Signing as")}:</span> <b>{data.signer.name}</b> ({data.signer.relationship})</p>}
        <p><span className="text-slate-500">{t("Provider organization")}:</span> <b>{data.legal_entity}</b></p>
      </div>
      <div className="grid gap-3 mt-6">
        <Button className="h-14 text-lg bg-[#0B3D8C] hover:bg-[#082F6B]" onClick={onYes} data-testid="identity-confirm-yes">{t("Yes, this is correct")}</Button>
        <Button variant="outline" className="h-14 text-lg" onClick={onNo} data-testid="identity-confirm-no">{t("No, something is wrong")}</Button>
      </div>
    </Ended>
  );
}
